import { Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { ContextualInfo, PicksView } from '@cds/betting-offer/domain-specific';
import { BetBuilderPricing } from '@cds/betting-offer/domain-specific/bet-builder';
import { PicksRequest } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { OddsComparerFactory } from '@frontend/sports/odds/feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Observable, filter, from, map } from 'rxjs';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipGroupInformation } from '../../../core/groups/betslip-group-information';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickId } from '../../../core/picks/pick-id';
import { IPrice, PickOddsState, PriceType } from '../../../core/picks/pick-models';
import { isBetBuilderPick } from '../../../core/utils';
import { ISingleBetPickState } from '../../single-bet/state';
import { EntainUiBetBuilderPickRemovalTracking, TrackEntainUiBetBuilderPickRemoval } from '../../tracking/tracking.models';
import { IBetslipTypeState } from '../../types/state';
import { BetslipPicksRequestBuilderService } from './betslip-pick-request-builder.service';

interface IPickSelected {
    isSelected: boolean;
}

export interface EnhancedFixtureData {
    fixture: Fixture;
    contextualInfo: ContextualInfo;
    betBuilderPricingGroups: Record<string, BetBuilderPricing>;
}

interface BetBuilderPricingGroups {
    [key: string]: BetBuilderPricing;
}

@Injectable()
export default class BetslipPickUtilsService {
    constructor(
        private oddsComparerFactory: OddsComparerFactory,
        private bettingOfferApi: BettingOfferApi,
        private pickRequestBuilder: BetslipPicksRequestBuilderService,
        private trackingService: TrackingService,
    ) {}

    compareAgainstLastPrice(pick: Pick<BetslipPick, 'oddsState' | 'currentPrice' | 'acceptedPrice' | 'priceHistory'>): number {
        // -1 new price is lower (odds down), 1 new price is bigger (odds up)
        if (pick && pick.oddsState === PickOddsState.Open) {
            if (pick.currentPrice!.type === PriceType.Fixed) {
                const oddsComparer = this.oddsComparerFactory.createComparer(pick.currentPrice!.nativeOdds);
                if (
                    pick.acceptedPrice &&
                    pick.acceptedPrice.price &&
                    pick.acceptedPrice.price.type === PriceType.Fixed &&
                    pick.acceptedPrice.isManuallyAccepted &&
                    oddsComparer.equal(pick.acceptedPrice.price.nativeOdds)
                ) {
                    return 0;
                }
                const lastPrice = this.findLastVisiblePrice(pick.priceHistory);

                if (lastPrice) {
                    return oddsComparer.greaterThan(lastPrice.nativeOdds) ? 1 : oddsComparer.lessThan(lastPrice.nativeOdds) ? -1 : 0;
                }
            }
        }

        return 0;
    }

    loadPickFromApi(pickId: PickId): Observable<EnhancedFixtureData> {
        const request = this.pickRequestBuilder.buildRequestFromPickId([pickId]);

        return from(this.bettingOfferApi.getPicksViewCache(request)).pipe(
            filter((response) => !!response?.fixturePage.fixtures.length),
            map(
                (response) =>
                    <EnhancedFixtureData>{
                        fixture: response!.fixturePage.fixtures[0],
                        contextualInfo: response!.contextualInfo,
                        betBuilderPricingGroups: response!.betBuilderPricingGroups,
                    },
            ),
        );
    }

    loadPicksFromPickIds(picksIds: PickId[], groupInfo: BetslipGroupInformation): Observable<PicksView> {
        const request = this.pickRequestBuilder.buildRequestFromPickId(picksIds, false, groupInfo);

        return this.loadPicksFromApi(request).pipe(filter((pickView) => !!pickView.fixturePage.fixtures.length));
    }

    loadBetBuilderPricingGroupsForPicks(picks: BetslipPick[]): Observable<BetBuilderPricingGroups> {
        const request = this.pickRequestBuilder.buildRequestFromPicks(picks, false);

        return this.loadPicksFromApi(request).pipe(map(({ betBuilderPricingGroups }) => betBuilderPricingGroups));
    }

    private loadPicksFromApi(request: PicksRequest): Observable<PicksView> {
        return from(this.bettingOfferApi.getPicksViewCache(request)).pipe(filter(isDefined));
    }

    getSelectedPicks(types: IBetslipTypeState, picksList: BetslipPick[]): BetslipPick[] {
        let picks: { [id: string]: IPickSelected } = {};
        switch (types.base.currentSelectedType) {
            case BetslipType.Combo:
                picks = types.comboBet.picks;
                break;
            case BetslipType.System:
                picks = types.systemBet.picks;
                break;
            default:
                picks = types.singleBet.picks;
                break;
        }

        return picksList.filter((p) => picks[p.id.toString()]?.isSelected);
    }

    getSelectedPicksAndGroups(types: IBetslipTypeState, picksList: BetslipPick[], isSportcastAsCombo: boolean): BetslipPick[] {
        let picks: { [id: string]: IPickSelected } = {};
        switch (types.base.currentSelectedType) {
            case BetslipType.Combo:
                picks = types.comboBet.picks;
                break;
            case BetslipType.System:
                picks = types.systemBet.picks;
                break;
            default:
                picks = types.singleBet.picks;
                break;
        }

        const isBetBuilder = isSportcastAsCombo ? isBetBuilderPick : BaseBetslipGroupPick.isPick;

        return picksList.filter((p) => isBetBuilder(p) || picks[p.id.toString()]?.isSelected);
    }

    isEachWaySelected(
        typeState: IBetslipTypeState,
        betslipType: BetslipType | null,
        singlePickOrId?: ISingleBetPickState | string,
        isLinear: boolean = false,
    ): boolean {
        switch (betslipType) {
            case BetslipType.Single: {
                const pick = typeof singlePickOrId === 'string' ? typeState.singleBet.picks[singlePickOrId] : singlePickOrId;

                return pick ? pick.isEachWay && pick.isSelected : Object.values(typeState.singleBet.picks).some((p) => p.isEachWay && p.isSelected);
            }
            case BetslipType.Combo: {
                return typeState.comboBet.isEachWay;
            }
            case BetslipType.System: {
                return isLinear ? Object.values(typeState.systemBet.linearTypeStakes).some((t) => t.isEachWay) : typeState.systemBet.isEachWay;
            }
            default:
                return false;
        }
    }

    private findLastVisiblePrice(priceHistory: ReadonlyArray<IPrice>): IPrice | null {
        const cnt = priceHistory.length;

        for (let i = cnt - 1; i >= 0; i--) {
            const price = priceHistory[i];
            if (price.isVisible && price.type === PriceType.Fixed) {
                return price;
            }
        }

        return null;
    }

    trackEntainUiBetBuilderPickRemoval(labelEvent: string) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: EntainUiBetBuilderPickRemovalTracking.TrackingSource,
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: EntainUiBetBuilderPickRemovalTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: EntainUiBetBuilderPickRemovalTracking.TrackingEventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.EVENT_NAME]: EntainUiBetBuilderPickRemovalTracking.TrackingEventName,
        } as TrackEntainUiBetBuilderPickRemoval);
    }

    trackNonSgpPickRemoval(labelEvent: string) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: EntainUiBetBuilderPickRemovalTracking.TrackingSource,
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: EntainUiBetBuilderPickRemovalTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }
}
